/**
 * @fileoverview Step 3 - Add new school
 * @description Form for adding a new school with name and zip code
 */

import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useForm } from '@mantine/form'
import { Button, Space, MultiSelect, TextInput, Text, Group, Divider } from '@mantine/core'
import { Heading } from 'components'
import { useNotification } from 'context'
import { useSignupContext } from '../context/SignupContext'
import { useGrades } from '../hooks/useGrades'
import { createExternalSchool, completeOnboarding } from '../service'
import { Form } from '../styles'
import { EmailVerificationModal } from './EmailVerificationModal'
import { GoogleSignupSuccessModal } from './GoogleSignupSuccessModal'

/**
 * Step 3 component - Add new school
 */
export const SignupStep3 = () => {
  const { t } = useTranslation()
  const { handleNotification } = useNotification()
  const { data, setStep, isGoogleAuth } = useSignupContext()
  const { gradeOptions } = useGrades()
  const [loading, setLoading] = useState(false)
  const [showEmailModal, setShowEmailModal] = useState(false)
  const [showGoogleModal, setShowGoogleModal] = useState(false)

  const form = useForm({
    initialValues: {
      grades: data.grades,
      schoolName: data.newSchoolName || '',
      zipCode: data.zipCode || '',
    },
    validateInputOnBlur: true,
    validate: {
      grades: value => (!value || value.length === 0 ? t('teacher_signup.validation_grade_required') : null),
      schoolName: value => (!value ? t('teacher_signup.validation_school_name_required') : null),
      zipCode: value => (!value ? t('teacher_signup.validation_zip_code_required') : null),
    },
  })

  /**
   * Handle form submission - creates school then completes onboarding
   * @param values - Form values
   */
  const handleSubmit = async (values: typeof form.values) => {
    setLoading(true)

    try {
      // Create external school first
      const school = await createExternalSchool(values.schoolName, values.zipCode)

      // Complete onboarding with new school (IDs are UUIDs)
      await completeOnboarding(school.id, values.grades)

      // Show appropriate modal based on auth type
      if (isGoogleAuth) {
        setShowGoogleModal(true)
      } else {
        setShowEmailModal(true)
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : t('teacher_signup.signup_error')
      handleNotification(true, errorMessage, true, 'error')
    } finally {
      setLoading(false)
    }
  }

  const handleSelectSchool = () => setStep(2)

  return (
    <>
      <EmailVerificationModal
        opened={showEmailModal}
        email={data.email}
        onClose={() => setShowEmailModal(false)}
      />
      <GoogleSignupSuccessModal
        opened={showGoogleModal}
        onClose={() => setShowGoogleModal(false)}
      />

      <div style={{ textAlign: 'center' }}>
        <Heading size='xl' text={t('teacher_signup.page_title')} />
      </div>
      <Text color='dimmed' align='center' mb='lg'>
        {t('teacher_signup.page_subtitle')}
      </Text>

      <Form onSubmit={form.onSubmit(handleSubmit)}>
        <MultiSelect
          size='md'
          label={t('teacher_signup.grade_label')}
          placeholder={t('teacher_signup.grade_placeholder')}
          data={gradeOptions}
          {...form.getInputProps('grades')}
        />

        <Space h='md' />

        <Group position='apart' mb='xs'>
          <Text weight={600}>{t('teacher_signup.add_new_school_title')}</Text>
          <Button variant='subtle' size='xs' color='teal' onClick={handleSelectSchool}>
            {t('teacher_signup.select_school_button')}
          </Button>
        </Group>

        <TextInput
          size='md'
          placeholder={t('teacher_signup.school_name_placeholder')}
          {...form.getInputProps('schoolName')}
        />

        <TextInput
          mt='md'
          size='md'
          placeholder={t('teacher_signup.zip_code_placeholder')}
          {...form.getInputProps('zipCode')}
        />

        <Divider my='lg' />

        <Button
          fullWidth
          size='lg'
          type='submit'
          loading={loading}
          radius='xl'
        >
          {t('teacher_signup.sign_up_button')}
        </Button>
      </Form>
    </>
  )
}
